import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { Card, Modal as AntModal, Select, Input } from 'antd';
import { Link } from 'react-router-dom';
import { numberWithCommas, isMobile } from 'utils';
import { projectsServices } from 'services';
import { CloseOutlined } from '@ant-design/icons';
import {
  getRewardDescriptionHtml,
  getRewardDescriptionText,
} from 'utils/rewardDescription';

const { Option } = Select;

// Helper functions
const buildEmptyAnswers = (reward) => {
  const variantCount = reward?.variants?.length || 0;
  const questionCount = reward?.questions?.length || 0;
  const extraCount = reward?.extra_details ? 1 : 0;
  return new Array(variantCount + questionCount + extraCount).fill('');
};

const getRewardImage = (reward) =>
  reward?.image ||
  reward?.banner ||
  reward?.cover ||
  reward?.thumbnail ||
  '/placeholder.png';

function GoodiesCard({
  open,
  reward,
  projectId,
  order,
  isTicketable,
  projectStatus,
  isPreview,
}) {
  const [configOpen, setConfigOpen] = useState(false);
  const [configReward, setConfigReward] = useState(null);
  const [configErrors, setConfigErrors] = useState({});

  const isFinished = useMemo(() => {
    if (!reward) return false;
    if (reward?.max_order_count === null || reward?.max_order_count === 0) {
      return false;
    }

    return reward?.ordered_reward_count >= reward?.max_order_count;
  }, [reward]);

  const imageSrc = useMemo(() => {
    if (!reward) return '';

    if (isMobile()) {
      return reward?.mobile_image || reward?.image || '';
    }

    return reward?.image || reward?.mobile_image || '';
  }, [reward]);

  const donationUrl = `/project/${projectId}/donation?donation=${
    isTicketable ? 0 : '20'
  }&rewardId=${reward?.id}`;
  const displayImageSrc = imageSrc || getRewardImage(reward);
  const cardDescription = getRewardDescriptionText(reward);

  const loadRewardConfiguration = async (rewardData) => {
    const preparedReward = { ...rewardData };

    const variantsRequest = await projectsServices.getRewardVariants(
      preparedReward.id
    );

    const questionsRequest = await projectsServices.getRewardQuestions(
      preparedReward.id
    );

    preparedReward.variants = variantsRequest?.[preparedReward.id] || [];
    preparedReward.questions = questionsRequest?.[preparedReward.id] || [];
    preparedReward.extra_details = variantsRequest?.extra_details || null;
    preparedReward.address_required =
      variantsRequest?.address_required || false;
    preparedReward.answers = buildEmptyAnswers(preparedReward);

    return preparedReward;
  };

  const closeConfigModal = () => {
    setConfigOpen(false);
    setConfigReward(null);
    setConfigErrors({});
  };

  const handleConfigValueChange = (value, answerIndex) => {
    setConfigReward((prev) => {
      const next = { ...prev };
      const answers = Array.isArray(next.answers) ? [...next.answers] : [];
      answers[answerIndex] = value;
      next.answers = answers;
      return next;
    });

    setConfigErrors((prev) => {
      const next = { ...prev };
      delete next[answerIndex];
      return next;
    });
  };

  const handleDetailsReward = async () => {
    try {
      const preparedReward = await loadRewardConfiguration(reward);
      setConfigReward(preparedReward);
      setConfigErrors({});
      setConfigOpen(true);
    } catch (error) {
      console.log(error);
    }
  };

  const renderConfigFields = () => {
    if (!configReward) return null;

    let cursor = 0;
    const fields = [];

    if (configReward?.variants?.length) {
      configReward.variants.forEach((variant, idx) => {
        const fieldIndex = cursor;
        fields.push(
          <FieldWrap key={`variant-${idx}`}>
            <FieldLabel>
              {variant.attribute}
              <span className="required">*</span>
            </FieldLabel>

            {variant?.options?.length ? (
              <>
                <Select
                  placeholder={`${variant.attribute} auswählen`}
                  value={configReward.answers?.[fieldIndex] || undefined}
                  onChange={(value) =>
                    handleConfigValueChange(value, fieldIndex)
                  }
                  size="large"
                >
                  {variant.options.map((option, k) => (
                    <Option key={k} value={option}>
                      {option}
                    </Option>
                  ))}
                </Select>
                {configErrors[fieldIndex] && (
                  <ErrorText>{configErrors[fieldIndex]}</ErrorText>
                )}
              </>
            ) : (
              <>
                <Input
                  placeholder={variant.attribute}
                  value={configReward.answers?.[fieldIndex] || ''}
                  onChange={(e) =>
                    handleConfigValueChange(e.target.value, fieldIndex)
                  }
                  size="large"
                />
                {configErrors[fieldIndex] && (
                  <ErrorText>{configErrors[fieldIndex]}</ErrorText>
                )}
              </>
            )}
          </FieldWrap>
        );
        cursor += 1;
      });
    }

    if (configReward?.questions?.length) {
      configReward.questions.forEach((question, idx) => {
        const fieldIndex = cursor;
        const label = Array.isArray(question) ? question[0] : 'Frage';

        fields.push(
          <FieldWrap key={`question-${idx}`}>
            <FieldLabel>
              {label}
              <span className="required">*</span>
            </FieldLabel>

            {Array.isArray(question) &&
              question.length > 1 &&
              question[1] !== '' ? (
              <>
                <Select
                  placeholder={label}
                  value={configReward.answers?.[fieldIndex] || undefined}
                  onChange={(value) =>
                    handleConfigValueChange(value, fieldIndex)
                  }
                  size="large"
                >
                  {question.map((answer, k) =>
                    k !== 0 ? (
                      <Option key={k} value={answer}>
                        {answer}
                      </Option>
                    ) : null
                  )}
                </Select>
                {configErrors[fieldIndex] && (
                  <ErrorText>{configErrors[fieldIndex]}</ErrorText>
                )}
              </>
            ) : (
              <>
                <Input
                  placeholder={label}
                  value={configReward.answers?.[fieldIndex] || ''}
                  onChange={(e) =>
                    handleConfigValueChange(e.target.value, fieldIndex)
                  }
                  size="large"
                />
                {configErrors[fieldIndex] && (
                  <ErrorText>{configErrors[fieldIndex]}</ErrorText>
                )}
              </>
            )}
          </FieldWrap>
        );

        cursor += 1;
      });
    }

    if (configReward?.extra_details) {
      const fieldIndex = cursor;
      fields.push(
        <FieldWrap key="extra-details">
          <FieldLabel>
            {configReward.extra_details}
            <span className="required">*</span>
          </FieldLabel>

          <Input
            placeholder={configReward.extra_details}
            value={configReward.answers?.[fieldIndex] || ''}
            onChange={(e) =>
              handleConfigValueChange(e.target.value, fieldIndex)
            }
            size="large"
          />

          {configErrors[fieldIndex] && (
            <ErrorText>{configErrors[fieldIndex]}</ErrorText>
          )}
        </FieldWrap>
      );
    }

    return fields;
  };

  return (
    <SingleRewardWrapper>
      <StyledCard
        className={[
          isFinished ? 'finished' : '',
          //isPopular ? 'most-wanted' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        hoverable={false}
        cover={
          <CoverImageFrame>
            <CoverImageBackground
              aria-hidden="true"
              alt=""
              src={displayImageSrc}
            />
            <CoverImageOverlay />
            <CoverImage
              alt={reward?.title || 'Goodie'}
              src={displayImageSrc}
            />
          </CoverImageFrame>
        }
      >
        <Title open={open}>{reward?.title}</Title>

        <Description open={open}>{cardDescription}</Description>

        {/*{!isSuccessful && open && (*/}
        {/*  <BookingButtonWrapper onClick={(e) => e.stopPropagation()}>*/}
        {/*    {isFinished ? (*/}
        {/*      <PrimaryButton disabled style={{ margin: '0 auto' }}>*/}
        {/*        Buchen*/}
        {/*      </PrimaryButton>*/}
        {/*    ) : (*/}
        {/*      <Link to={donationUrl}>*/}
        {/*        <PrimaryButton style={{ margin: '0 auto' }}>*/}
        {/*          Buchen*/}
        {/*        </PrimaryButton>*/}
        {/*      </Link>*/}
        {/*    )}*/}
        {/*  </BookingButtonWrapper>*/}
        {/*)}*/}

        <FooterRow>
          <Price>
            {numberWithCommas(Number(reward?.price || 0).toFixed(2))} €
          </Price>
          {/*<Numbers>*/}
          {/*  {reward?.ordered_reward_count || '0'}{' '}*/}
          {/*  {reward?.max_order_count !== null && (*/}
          {/*    <span>von {reward?.max_order_count || '0'}</span>*/}
          {/*  )}{' '}*/}
          {/*  gebucht*/}
          {/*</Numbers>*/}

          {!isFinished && (
            <ButtonsWrapper>
              <DetailButtonSmall type="button" onClick={handleDetailsReward}>
                Details
              </DetailButtonSmall>
              {isPreview ? (
                <ButtonToGo as="button" type="button" disabled>
                  Auswählen
                </ButtonToGo>
              ) : (
                <ButtonToGo to={donationUrl}>Auswählen</ButtonToGo>
              )}
            </ButtonsWrapper>
          )}
        </FooterRow>

        {/*<DeliveryInfo>*/}
        {/*  {!isTicketable*/}
        {/*    ? reward?.shipping_date*/}
        {/*      ? 'Sofort lieferbar'*/}
        {/*      : 'Nach erfolgreicher Finanzierungsphase lieferbar'*/}
        {/*    : null}*/}
        {/*</DeliveryInfo>*/}

        {/*{isPopular && <PopularBadge>Beliebt</PopularBadge>}*/}
      </StyledCard>

      {/* Details Modal */}
      <AntModal
        open={configOpen}
        footer={null}
        closable={false}
        onCancel={closeConfigModal}
        width={520}
        centered
        destroyOnClose
      >
        {configReward && (
          <ModalInner>
            <ModalClose type="button" onClick={closeConfigModal}>
              <CloseOutlined />
            </ModalClose>

            <ModalTitle>{configReward.title}</ModalTitle>

            <ModalText
              dangerouslySetInnerHTML={{
                __html: getRewardDescriptionHtml(
                  configReward,
                  'Weitere Informationen zu diesem Goodie'
                ),
              }}
            />

            <ModalImageWrap>
              <img
                src={getRewardImage(configReward)}
                alt={configReward.title}
              />
            </ModalImageWrap>

            <ModalPrice>
              {parseFloat(configReward.price || 0).toFixed(2)} €
            </ModalPrice>

            <ModalAvailability>
              {configReward?.max_order_count &&
              configReward?.ordered_reward_count != null
                ? `${configReward.ordered_reward_count} von ${configReward.max_order_count} gebucht`
                : 'Noch verfügbar'}
            </ModalAvailability>

            {renderConfigFields()}

            <ModalFooter>
              <ModalGhostButton type="button" onClick={closeConfigModal}>
                Schließen
              </ModalGhostButton>
              {isPreview ? (
                <ModalSelectButton as="button" type="button" disabled>
                  Auswählen
                </ModalSelectButton>
              ) : (
                <ModalSelectButton to={donationUrl}>
                  Auswählen
                </ModalSelectButton>
              )}
            </ModalFooter>
          </ModalInner>
        )}
      </AntModal>
    </SingleRewardWrapper>
  );
}

GoodiesCard.propTypes = {
  open: PropTypes.bool,
  reward: PropTypes.object,
  projectId: PropTypes.number,
  order: PropTypes.number,
  openCards: PropTypes.func,
  isTicketable: PropTypes.bool,
  projectStatus: PropTypes.string,
  isPreview: PropTypes.bool,
};



const SingleRewardWrapper = styled.div`
  text-align: center;
`;

const StyledCard = styled(Card)`
  border: 1px solid #E8E8E8;
  padding: 16px;
  border-radius: 12px !important;
  overflow: hidden;
  text-align: start;
  position: relative;

  &.finished {
    opacity: 0.7;
  }

  &.most-wanted {
    border: 2px solid #95bd51;
  }

  .ant-card-body {
    padding: 20px;
  }
  
  @media (max-width: 720px) {
    padding: 0px;
    
    
    .ant-card-body {
      padding: 16px;
    }
    
  }
  
`;

const CoverImageFrame = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #f0f0f0;
  border-radius: 0px 16px 0px 16px !important;
`;

const CoverImageBackground = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: blur(8px);
  transform: scale(1.08);
  opacity: 0.9;
`;

const CoverImageOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.18);
`;

const CoverImage = styled.img`
  position: absolute;
  inset: 0;
  z-index: 1;
  display: block;
  width: 100% !important;
  height: 100% !important;
  object-fit: contain !important;
  object-position: center;
`;

const Title = styled.div`
  font-size: 20px;
  font-weight: 600;
  line-height: 30px;;
  color: #181D27;
  word-break: ${({ open }) => (open ? 'break-word' : 'normal')};
  text-overflow: ${({ open }) => (open ? 'unset' : 'ellipsis')};
  white-space: ${({ open }) => (open ? 'normal' : 'nowrap')};
  overflow: ${({ open }) => (open ? 'visible' : 'hidden')};
  text-align: center;
`;

const Description = styled.div`
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  color: #656565;
  margin-top: 6px;
  word-break: ${({ open }) => (open ? 'break-word' : 'normal')};
  text-overflow: ${({ open }) => (open ? 'unset' : 'ellipsis')};
  white-space: ${({ open }) => (open ? 'normal' : 'nowrap')};
  overflow: ${({ open }) => (open ? 'visible' : 'hidden')};
  text-align: center;
`;


const FooterRow = styled.div`
  margin-top: 16px;
  display: flex;

  gap: 16px;
  width: 100%;
  align-items: center;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 720px) {
    flex-direction: column;
    justify-content: space-between;
    gap: 12px;
    align-items: center;
    text-align: left;
    width: 100%;
  }
`;


const Price = styled.span`
  color: #181D27;
  text-align: center;
  font-family: 'Inter', sans-serif;
  line-height: 30px;
  font-size: 20px;
  font-weight: 600;
  @media (max-width: 720px) {
    text-align: center;
  }
`;
const ButtonToGo = styled(Link)`
  /* Buttons/Button */

  box-sizing: border-box;
  cursor: pointer;
  /* Auto layout */
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 12px 18px;
  gap: 6px;

  width: 50%;
  height: 48px;
  color: #ffffff !important;

  background: #9dbc60;

  box-shadow: 0px 1px 2px rgba(10, 13, 18, 0.05),
    inset 0px 0px 0px 1px rgba(10, 13, 18, 0.18),
    inset 0px -2px 0px rgba(10, 13, 18, 0.05);
  border-radius: 8px;
  transition: background 0.2s ease;
  &:hover {
    background: #3f621a;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
    background: #9dbc60;
  }

  /* Text */
  /* Text md/Semibold */
  font-family: 'Inter';
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  /* identical to box height, or 150% */

  color: #ffffff;

  /* Inside auto layout */
  flex: none;
  order: 0;
  flex-grow: 0;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
  align-items: center;
`;

const DetailButtonSmall = styled.button`
  flex: 1;
  box-sizing: border-box;
  height: 48px;
  width: 50%;
  border: 1px solid #d5d7da;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0px 1px 2px rgba(10, 13, 18, 0.05),
    inset 0px 0px 0px 1px rgba(10, 13, 18, 0.18),
    inset 0px -2px 0px rgba(10, 13, 18, 0.05);
  cursor: pointer;
  transition: background 0.2s ease;
  &:hover {
    background: #f8f9fb;
  }
  
  /* Text md/Semibold */
  font-family: 'Inter';
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  /* identical to box height, or 150% */

  color: #414651;
  
`;

const FieldWrap = styled.div`
  margin-bottom: 14px;
`;

const FieldLabel = styled.div`
  margin-bottom: 6px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: #414651;
  font-weight: 500;

  .required {
    color: #9dbc60;
    margin-left: 4px;
  }
`;

const ErrorText = styled.div`
  margin-top: 6px;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  line-height: 18px;
  color: #d92d20;
`;

const ModalInner = styled.div`
  position: relative;
  padding-top: 6px;
`;

const ModalClose = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  border: none;
  background: transparent;
  font-size: 24px;
  color: #a4a7ae;
  cursor: pointer;
`;

const ModalTitle = styled.h3`
  margin: 0 40px 8px 0;
  font-family: 'Inter', sans-serif;
  font-size: 20px;
  line-height: 40px;
  font-weight: 600;
  color: #181d27;
`;

const ModalText = styled.div`
  margin: 0 0 20px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: #535862;

  > :first-child {
    margin-top: 0;
  }

  > :last-child {
    margin-bottom: 0;
  }
`;

const ModalImageWrap = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  margin: 12px 0 20px;

  img {
    width: 220px;
    max-width: 100%;
    height: auto;
    object-fit: contain;
    display: block;
  }
`;

const ModalPrice = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 20px;
  line-height: 30px;
  font-weight: 600;
  color: #181d27;
  margin-bottom: 4px;
`;

const ModalAvailability = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: #717680;
  margin-bottom: 20px;
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
  align-items: stretch;

  @media (max-width: 767px) {
    flex-direction: column-reverse;
  }
`;

const ModalGhostButton = styled.button`
  flex: 1;
  box-sizing: border-box;
  min-height: 48px;
  width: 100%;
  border: 1px solid #d5d7da;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0px 1px 2px rgba(10, 13, 18, 0.05),
    inset 0px 0px 0px 1px rgba(10, 13, 18, 0.18),
    inset 0px -2px 0px rgba(10, 13, 18, 0.05);
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: #414651;
  cursor: pointer;
`;

const ModalSelectButton = styled(Link)`
  flex: 1;
  box-sizing: border-box;
  min-height: 48px;
  width: 100%;
  border: none;
  background: #9dbc60;
  border-radius: 8px;
  box-shadow: 0px 1px 2px rgba(10, 13, 18, 0.05),
    inset 0px 0px 0px 1px rgba(10, 13, 18, 0.18),
    inset 0px -2px 0px rgba(10, 13, 18, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: #ffffff !important;
  cursor: pointer;

  &:hover {
    background: #3f621a;
    color: #ffffff !important;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
    background: #9dbc60;
  }
`;



export default GoodiesCard;
